function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

export function renderLoading(root) {
  root.replaceChildren(el(`<div class="state state-loading"><div class="spinner"></div><p>Loading trip…</p></div>`));
}

export function renderError(root, { title, message, action }) {
  const node = el(`
    <div class="state state-error">
      <h1>${escape(title)}</h1>
      <p>${escape(message)}</p>
      ${action ? `<button class="btn btn-secondary">${escape(action.label)}</button>` : ''}
    </div>
  `);
  if (action) node.querySelector('button').addEventListener('click', action.onClick);
  root.replaceChildren(node);
}

export function renderInAppWarning(root, url) {
  const node = el(`
    <div class="state state-warn">
      <h1>Please open in Chrome</h1>
      <p>This trip tracker needs to run in Chrome (or another full browser) to track your location reliably. The in-app browser you opened the link from is missing required features.</p>
      <button class="btn btn-primary" id="copy">Copy link</button>
      <p class="hint">Then paste it into Chrome.</p>
    </div>
  `);
  node.querySelector('#copy').addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(url); node.querySelector('#copy').textContent = 'Copied!'; }
    catch { prompt('Copy this URL:', url); }
  });
  root.replaceChildren(node);
}

export function renderIdle(root, { label, onStart }) {
  const node = el(`
    <div class="state state-idle">
      <h1>${escape(label || 'Trip')}</h1>
      <p class="explainer">We'll track your location while this trip is active. Before you start, please confirm:</p>
      <ul class="checklist">
        <li><label><input type="checkbox" data-chk="1"> Phone is plugged into a charger</label></li>
        <li><label><input type="checkbox" data-chk="2"> Battery Saver is OFF</label></li>
        <li><label><input type="checkbox" data-chk="3"> Do Not Disturb is OFF</label></li>
        <li><label><input type="checkbox" data-chk="4"> I'll keep this screen on during the trip</label></li>
      </ul>
      <button class="btn btn-primary btn-big" disabled>Start Trip</button>
      <p class="warn">Tracking only works while this page is open. Don't lock your phone.</p>
    </div>
  `);
  const boxes = node.querySelectorAll('input[type=checkbox]');
  const btn = node.querySelector('button');
  const refresh = () => {
    btn.disabled = !Array.from(boxes).every(b => b.checked);
  };
  boxes.forEach(b => b.addEventListener('change', refresh));
  btn.addEventListener('click', () => {
    btn.disabled = true;
    btn.textContent = 'Getting first fix…';
    onStart();
  });
  root.replaceChildren(node);
}

export function renderStarting(root) {
  root.replaceChildren(el(`
    <div class="state state-loading">
      <div class="spinner"></div>
      <p>Getting first GPS fix and starting trip…</p>
    </div>
  `));
}

export function renderActive(root, { label, onEnd }) {
  const node = el(`
    <div class="state state-active">
      <div class="status-bar">
        <span class="dot pulsing"></span>
        <span class="status-text">TRACKING ACTIVE</span>
      </div>
      <h2 class="trip-label">${escape(label || 'Trip')}</h2>
      <div class="metrics">
        <div class="metric"><span class="metric-label">Accuracy</span><span class="metric-val" data-k="accuracy">—</span></div>
        <div class="metric"><span class="metric-label">Pings queued</span><span class="metric-val" data-k="queue">0</span></div>
        <div class="metric"><span class="metric-label">Last fix</span><span class="metric-val" data-k="last">—</span></div>
      </div>
      <div class="banner banner-hidden" data-k="hidden-banner" hidden></div>
      <div class="banner banner-error" data-k="error-banner" hidden></div>
      <div class="reminder">Keep this screen on. Do not lock your phone.</div>
      <button class="btn btn-danger btn-big" data-k="end">End Trip</button>
    </div>
  `);
  node.querySelector('[data-k=end]').addEventListener('click', () => onEnd());
  root.replaceChildren(node);
  return {
    setAccuracy(m) { setText(node, 'accuracy', m == null ? '—' : `${Math.round(m)} m`); },
    setQueue(n) { setText(node, 'queue', String(n)); },
    setLastFix(ts) {
      setText(node, 'last', ts ? new Date(ts).toLocaleTimeString() : '—');
    },
    showHiddenWarning(ms) {
      const b = node.querySelector('[data-k=hidden-banner]');
      b.textContent = `App was in background for ${Math.round(ms / 1000)}s — tracking paused during that time.`;
      b.hidden = false;
    },
    showError(msg) {
      const b = node.querySelector('[data-k=error-banner]');
      b.textContent = msg;
      b.hidden = false;
    },
    clearError() {
      node.querySelector('[data-k=error-banner]').hidden = true;
    },
  };
}

export function renderEnding(root) {
  root.replaceChildren(el(`
    <div class="state state-loading">
      <div class="spinner"></div>
      <p>Finishing trip…</p>
    </div>
  `));
}

export function renderEnded(root, { onInstall, canInstall }) {
  const node = el(`
    <div class="state state-ended">
      <div class="check">✓</div>
      <h1>Trip ended</h1>
      <p>Thanks. You can close this page.</p>
      ${canInstall ? '<button class="btn btn-secondary" data-k="install">Install for one-tap access</button>' : ''}
    </div>
  `);
  if (canInstall) node.querySelector('[data-k=install]').addEventListener('click', onInstall);
  root.replaceChildren(node);
}

export function renderPermissionDenied(root, { onRetry }) {
  const node = el(`
    <div class="state state-error">
      <h1>Location access blocked</h1>
      <p>This trip needs location access. To fix:</p>
      <ol>
        <li>Tap the lock icon in the address bar.</li>
        <li>Tap <b>Permissions</b> or <b>Site settings</b>.</li>
        <li>Set <b>Location</b> to <b>Allow</b>.</li>
        <li>Reload this page.</li>
      </ol>
      <button class="btn btn-primary">Reload</button>
    </div>
  `);
  node.querySelector('button').addEventListener('click', onRetry);
  root.replaceChildren(node);
}

function setText(root, key, value) {
  const e = root.querySelector(`[data-k=${key}]`);
  if (e) e.textContent = value;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
